<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { auth, confirmPasswordReset, verifyPasswordResetCode } from "@/firebase";
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import { logError } from "@/utils/secureLogger";

onMounted(() => {
  document.title = "ElevatED – Reset Password";
});

const router = useRouter();
const route = useRoute();

const newPassword = ref("");
const confirmPassword = ref("");
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const errorMessage = ref("");
const successMessage = ref("");
const isResetting = ref(false);
const oobCode = ref("");

// Password validation
const passwordLength = computed(() => newPassword.value.length >= 8 && newPassword.value.length <= 64);
const hasUppercase = computed(() => /[A-Z]/.test(newPassword.value));
const hasLowercase = computed(() => /[a-z]/.test(newPassword.value));
const hasNumber = computed(() => /\d/.test(newPassword.value));
const hasSpecialChar = computed(() => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword.value));

const isFormValid = computed(
  () =>
    passwordLength.value &&
    hasUppercase.value &&
    hasLowercase.value &&
    hasNumber.value &&
    hasSpecialChar.value &&
    newPassword.value === confirmPassword.value &&
    newPassword.value.length > 0
);

onMounted(() => {
  // Get the oobCode from URL parameters
  // Firebase can send the code in different parameter names
  const urlParams = new URLSearchParams(window.location.search);
  oobCode.value = urlParams.get("oobCode") || urlParams.get("oobcode") || urlParams.get("code") || "";

  // Also check for Firebase's action code in the URL hash
  if (!oobCode.value && window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    oobCode.value = hashParams.get("oobCode") || hashParams.get("oobcode") || hashParams.get("code") || "";
  }

  if (!oobCode.value) {
    errorMessage.value = "Invalid password reset link. Please request a new one.";
  }
});

const handlePasswordReset = async () => {
  if (!oobCode.value) {
    errorMessage.value = "Invalid password reset link. Please request a new one.";
    return;
  }

  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = "Passwords do not match.";
    return;
  }

  if (!isFormValid.value) {
    errorMessage.value = "Please ensure your password meets all requirements.";
    return;
  }

  isResetting.value = true;
  errorMessage.value = "";

  try {
    // Verify the reset code first - Firebase validates expiration here
    await verifyPasswordResetCode(auth, oobCode.value);

    // Reset the password
    await confirmPasswordReset(auth, oobCode.value, newPassword.value);

    successMessage.value = "Password successfully reset! Redirecting to login...";

    // Redirect to login after 2 seconds
    setTimeout(() => {
      router.push("/login");
    }, 2000);
  } catch (error: any) {
    logError("Password reset error", error);

    if (error.code === "auth/expired-action-code") {
      errorMessage.value = "This password reset link has expired. Please request a new one.";
    } else if (error.code === "auth/invalid-action-code") {
      errorMessage.value = "Invalid password reset link. Please request a new one.";
    } else if (error.code === "auth/weak-password") {
      errorMessage.value = "Password is too weak. Please choose a stronger password.";
    } else if (error.code === "auth/user-disabled") {
      errorMessage.value = "This account has been disabled. Please contact support.";
    } else if (error.code === "auth/user-not-found") {
      errorMessage.value = "No account found with this email address.";
    } else if (error.code === "auth/network-request-failed") {
      errorMessage.value = "Network error. Please check your connection and try again.";
    } else if (error.code === "auth/too-many-requests") {
      errorMessage.value = "Too many attempts. Please try again later.";
    } else {
      errorMessage.value = `Failed to reset password: ${error.message || "Please try again."}`;
    }
  } finally {
    isResetting.value = false;
  }
};
</script>

<style scoped>
.login-container {
  box-shadow: 8px 8px 0px #000;
  max-width: 800px;
  margin: 0 auto;
}
</style>

<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Background using login.png -->
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style="background-image: url('/login.png')"
    >
      <!-- Overlay to ensure text readability -->
      <div class="absolute inset-0 bg-blue-900/20"></div>
    </div>

    <!-- Decorative floating shapes -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <!-- Star shape top left -->
      <div class="absolute top-20 left-16 text-yellow-400 text-3xl animate-pulse">✦</div>
      <!-- Circle top right -->
      <div class="absolute top-32 right-20 w-4 h-4 bg-pink-400 rounded-full animate-bounce"></div>
      <!-- Plus bottom left -->
      <div class="absolute bottom-40 left-12 text-green-400 text-2xl animate-pulse">✚</div>
      <!-- Diamond right -->
      <div
        class="absolute top-1/2 right-16 w-6 h-6 bg-purple-400 transform rotate-45 animate-pulse"
      ></div>
      <!-- Circle bottom right -->
      <div
        class="absolute bottom-20 right-32 w-5 h-5 bg-orange-400 rounded-full animate-bounce"
      ></div>
      <!-- Clover left -->
      <div class="absolute top-1/3 left-20 text-green-500 text-2xl animate-pulse">♣</div>
      <!-- Star right -->
      <div class="absolute top-1/4 right-1/4 text-white text-xl animate-pulse">✦</div>
    </div>

    <!-- Content -->
    <div class="relative z-10 flex min-h-screen items-center justify-center px-6 py-4 pb-8 lg:px-8">
      <div class="w-full max-w-md mt-10 mb-15">
        <!-- Header -->
        <div class="text-center mb-4">
          <h1 class="text-5xl font-black text-white mb-2 tracking-wide">
            Reset Your <span class="font-black text-blue-200">Password</span>
          </h1>
          <p class="text-xl text-white font-light italic tracking-wide">
            Enter your new password below.
          </p>
        </div>

        <!-- Password Reset Form Container -->
        <div
          class="login-container bg-white rounded-3xl shadow-2xl p-10 border-4 border-black relative"
        >
          <form class="space-y-5" @submit.prevent="handlePasswordReset">
            <!-- New Password Field -->
            <div>
              <label for="newPassword" class="block text-sm font-medium text-gray-600 mb-2">
                New Password
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <input
                  v-model="newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  name="newPassword"
                  id="newPassword"
                  autocomplete="new-password"
                  required
                  placeholder="Enter your new password"
                  class="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-gray-50"
                />
                <button
                  type="button"
                  @click="showNewPassword = !showNewPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                >
                                    <!-- Eye Slash Icon (when password is visible - click to hide) -->
                  <svg
                    v-if="showNewPassword"
                    class="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                    <line
                      x1="4"
                      y1="6"
                      x2="20"
                      y2="18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  <!-- Eye Icon (when password is hidden - click to show) -->
                  <svg
                    v-else
                    class="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Confirm Password Field -->
            <div>
              <label for="confirmPassword" class="block text-sm font-medium text-gray-600 mb-2">
                Confirm New Password
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <input
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  name="confirmPassword"
                  id="confirmPassword"
                  autocomplete="new-password"
                  required
                  placeholder="Confirm your new password"
                  class="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-gray-50"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                >
                  <!-- Eye Slash Icon (when password is visible - click to hide) -->
                  <svg
                    v-if="showConfirmPassword"
                    class="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                    <line
                      x1="4"
                      y1="6"
                      x2="20"
                      y2="18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  <!-- Eye Icon (when password is hidden - click to show) -->
                  <svg
                    v-else
                    class="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Password Requirements -->
            <div class="text-sm text-gray-600">
              <p class="font-medium mb-2">Password must contain:</p>
              <ul class="space-y-1">
                <li class="flex items-center">
                  <svg
                    v-if="passwordLength"
                    class="h-4 w-4 text-green-500 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <svg
                    v-else
                    class="h-4 w-4 text-gray-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  Between 8-64 characters
                </li>
                <li class="flex items-center">
                  <svg
                    v-if="hasUppercase"
                    class="h-4 w-4 text-green-500 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <svg
                    v-else
                    class="h-4 w-4 text-gray-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  One uppercase letter
                </li>
                <li class="flex items-center">
                  <svg
                    v-if="hasLowercase"
                    class="h-4 w-4 text-green-500 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <svg
                    v-else
                    class="h-4 w-4 text-gray-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  One lowercase letter
                </li>
                <li class="flex items-center">
                  <svg
                    v-if="hasNumber"
                    class="h-4 w-4 text-green-500 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <svg
                    v-else
                    class="h-4 w-4 text-gray-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  One number
                </li>
                <li class="flex items-center">
                  <svg
                    v-if="hasSpecialChar"
                    class="h-4 w-4 text-green-500 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <svg
                    v-else
                    class="h-4 w-4 text-gray-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  One special character
                </li>
              </ul>
            </div>

            <!-- Error Message -->
            <div
              v-if="errorMessage"
              class="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {{ errorMessage }}
            </div>

            <!-- Success Message -->
            <div
              v-if="successMessage"
              class="text-green-600 text-sm bg-green-50 border border-green-200 rounded-lg px-3 py-2"
            >
              {{ successMessage }}
            </div>

            <!-- Reset Password Button -->
            <div class="pt-4">
              <button
                type="submit"
                :disabled="!isFormValid || isResetting"
                class="group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg transition-all duration-200 shadow-lg text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <span v-if="isResetting">Resetting Password...</span>
                <span v-else class="flex items-center">
                  Reset Password
                  <svg
                    class="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </form>

          <!-- Back to Login -->
          <div class="mt-6 text-center">
            <router-link to="/login" class="text-sm text-blue-600 hover:text-blue-500">
              ← Back to Login
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
